import { imageUrl } from '../../utils/imageUrl.js';
import { imageFrameStyle, imageFrameBox } from '../../utils/imageFrame.js';
import { handlePresenterImageError } from '../../config/homeImages.js';
// src/components/home/HeroPresenter.jsx
//
// The hero's presenter block (home-4a-hero): Danielle opens the page, in whichever
// form she has material for.
//
// THREE STATES, ONE ALWAYS CHOSEN (presenterState in homeSections.js):
//   video       -- homeIntroVideoUrl, as a click-to-load 16:9 embed
//   photo       -- profilePhotoUrl, as a portrait card
//   credentials -- the four CREDENTIAL_TILES in one compact row
//
// The point of the third state is that the hero is COMPLETE with no video and no
// photo: nothing here waits on filming, and there is never an empty slot or a
// "coming soon" placeholder. Pasting a YouTube URL into the existing Site Text field
// upgrades the hero with no deploy.
import React, { useState } from 'react';
import { STUDENT_ACHIEVEMENTS, homeRole } from './marketingCopy.js';
import YouTubeEmbed from '../YouTubeEmbed.jsx';
import { CREDENTIAL_TILES } from '../../config/siteCredentials.js';

const CARD = 'rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)]';

export default function HeroPresenter({ state, siteText }) {
    const st = siteText || {};
    const name = st.aboutName || '';
    const role = homeRole(st.aboutRole);
    const [index, setIndex] = useState(0);
    const photos = (Array.isArray(st.heroGallery) ? st.heroGallery : []).filter(p => p?.imageUrl);
    const count = Math.min(4, Math.max(1, Number(st.heroGalleryColumns) || 3));
    const layout = st.heroGalleryLayout || "featured";
    const start = Math.min(index, Math.max(0, photos.length - 1));
    const shown = photos.slice(start, start + count);

    if (state.kind === 'video') {
        return (
            <div className={CARD + ' overflow-hidden p-2'}>
                {/* Click-to-load: no third-party frame, and no autoplay, until asked. */}
                <YouTubeEmbed url={state.videoUrl} title={name ? name + ' - intro' : 'Intro video'}
                    posterSizes="(min-width: 1024px) 500px, calc(100vw - 52px)" priority />
                <div className="px-2 pt-2 pb-1 font-display text-book-muted text-xs font-semibold">
                    {name ? name.split(' ').slice(-2).join(' ') : 'Danielle'} &middot; intro
                </div>
            </div>
        );
    }

    if (state.kind === 'photo' && photos.length) {
        return (
            <div className={CARD + ' overflow-hidden'}>
                <div className="grid gap-3 p-3 max-w-lg mx-auto items-stretch" style={{ gridTemplateColumns: layout === "stacked" ? "minmax(0,1fr)" : `repeat(${layout === "featured" && shown.length > 1 ? 2 : shown.length}, minmax(0,1fr))` }}>
                    {shown.map((photo, i) => <figure key={start + i} style={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gridRow: layout === 'featured' && i === 0 && shown.length > 1 ? `span ${shown.length - 1}` : undefined }}>
                        <div className="bg-book-bg overflow-hidden rounded" style={layout === 'featured' && i === 0 && shown.length > 1 ? { position: 'relative', flex: '1 1 0', minHeight: 0 } : imageFrameBox(photo.imageUrlFrame)}><img src={imageUrl(photo.imageUrl)} onError={handlePresenterImageError} style={{ ...imageFrameStyle(photo.imageUrlFrame), ...(layout === 'featured' && i === 0 && shown.length > 1 ? { position: 'absolute', inset: 0 } : {}) }} referrerPolicy="no-referrer" alt={photo.caption || name || 'Gallery photo'} className="w-full h-full object-cover" /></div>
                        {photo.caption && <figcaption className="text-xs mt-1 leading-snug">{photo.caption}</figcaption>}
                    </figure>)}
                </div>
                <div className="px-4 pb-3">
                    {photos.length > count && <div className="flex justify-between items-center text-xs mb-2"><button className="border rounded px-2 py-1" aria-label="Previous gallery photos" onClick={() => setIndex(Math.max(0, start - count))} disabled={!start}>←</button><span>{start + 1}–{Math.min(start + count, photos.length)} / {photos.length}</span><button className="border rounded px-2 py-1" aria-label="Next gallery photos" onClick={() => setIndex(start + count < photos.length ? start + count : 0)}>→</button></div>}
                    <div className="font-display text-book-text text-sm font-bold leading-tight">{name || 'Danielle'}</div>
                    {role && <div className="font-serif italic text-book-muted text-xs mt-0.5">{role}</div>}
                    <div className="text-book-muted text-xs mt-1">Television presenter on RTHK 31 &amp; 32</div>
                </div>
            </div>
        );
    }

    // credentials -- the default, and a complete hero in its own right.
    return (
        <div className={CARD + ' p-4'}>
            {role && (
                <div className="font-display text-book-muted text-xs font-semibold mb-3">{role}</div>
            )}
            <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-3">
                {CREDENTIAL_TILES.map((c, i) => (
                    <div key={i} className="text-center">
                        <div className="font-display text-book-accent text-base font-bold leading-snug">{c.n}</div>
                        <div className="font-serif text-book-muted text-xs mt-1 leading-snug">{c.l}</div>
                    </div>
                ))}
            </div>
            <p className="border-t border-book-line mt-4 pt-3 font-serif text-book-muted text-xs leading-relaxed">{STUDENT_ACHIEVEMENTS}</p>
        </div>
    );
}
